import React, { KeyboardEvent, MouseEvent } from "react";
import cn from "classnames";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import saveGreenRightIcon from "@src/assets/svg/save-green-right-icon.svg";
import closeRedIcon from "@src/assets/svg/close-red-icon.svg";
import { useI18N, useLanguage } from "@src/language/language-context";
import {
  GatewayOperationError,
  MachineAvailabilityStatus,
  getGatewayOperationProtocolEntries,
} from "@src/types/gateway-onboarding";
import styles from "./machine-availability-status.module.scss";
import { OperationProtocolDetails } from "@src/components/atoms/operation-protocol-details/operation-protocol-details";
import { getOpcUaErrorDescriptions } from "@src/cloud-connect/utils/opcua-status-codes";

const cssClassName = "machine-availability-status";

interface MachineAvailabilityStatusProps {
  status: MachineAvailabilityStatus;
  isCreatingMachine?: boolean;
  skillsLabel?: string;
  errorMessage?: string;
  hideSupportActions?: boolean;
  failureDetails?: GatewayOperationError | null;
  showNoSkillsIdentified?: boolean;
  showRetryForMissingSkills?: boolean;
  showProtocolDetails?: boolean;
  onShowProtocol?: () => void;
  onGetSupport?: () => void;
  onRetry?: () => void;
}

export const MachineAvailabilityStatusPanel = ({
  status,
  isCreatingMachine,
  skillsLabel,
  errorMessage,
  failureDetails,
  showNoSkillsIdentified,
  showRetryForMissingSkills,
  showProtocolDetails,
  hideSupportActions,
  onShowProtocol,
  onGetSupport,
  onRetry,
}: MachineAvailabilityStatusProps) => {
  const i18n = useI18N();
  const language = useLanguage();

  const texts = {
    machineIpReachable: i18n(
      "modules.addNewMachineDrawerModal.machineIpReachable",
    ),
    checkingMachineAvailability: i18n(
      "modules.addNewMachineDrawerModal.checkingMachineAvailability",
    ),
    creatingMachine: i18n("modules.addNewMachineDrawerModal.creatingMachine"),
    skillsIdentified: i18n("modules.addNewMachineDrawerModal.skillsIdentified"),
    skillsNotAvailableErrorMessage: i18n(
      "modules.addNewMachineDrawerModal.skillsNotAvailableErrorMessage",
    ),
    noMachineSkillsFoundMessage: i18n(
      "modules.addNewMachineDrawerModal.noMachineSkillsFoundMessage",
    ),
    showProtocol: i18n("modules.addNewMachineDrawerModal.showProtocol"),
    hideProtocol: i18n("modules.addNewMachineDrawerModal.hideProtocol"),
    getSupport: i18n("modules.addNewMachineDrawerModal.getSupport"),
    retry: i18n("modules.addNewMachineDrawerModal.summaryRetryButton"),
    opcUaErrorDetailsTitle: i18n(
      "modules.addNewMachineDrawerModal.opcUaErrorDetailsTitle",
    ),
    statusIconAltText: i18n(
      "modules.addNewMachineDrawerModal.statusIconAltText",
    ),
  };

  if (status === MachineAvailabilityStatus.IDLE) {
    return null;
  }

  const handleFooterButtonClick =
    (handler?: () => void) =>
    (e: MouseEvent | KeyboardEvent): void => {
      e.preventDefault();
      handler?.();
    };

  const renderStatusRow = (
    icon: React.ReactNode,
    text: string,
    textVariant?: "success" | "error" | "loading" | "warning",
    isExpanded?: boolean,
  ) => (
    <div className={styles[`${cssClassName}__status-row`]}>
      <span className={styles[`${cssClassName}__status-icon`]}>{icon}</span>
      <span
        className={cn(styles[`${cssClassName}__status-text`], {
          [styles[`${cssClassName}__status-text--success`]]:
            textVariant === "success",
          [styles[`${cssClassName}__status-text--error`]]:
            textVariant === "error",
          [styles[`${cssClassName}__status-text--warning`]]:
            textVariant === "warning",
          [styles[`${cssClassName}__status-text--loading`]]:
            textVariant === "loading",
          [styles[`${cssClassName}__status-text--error-expanded`]]:
            Boolean(isExpanded),
        })}
        title={text}
      >
        {text}
      </span>
    </div>
  );

  if (status === MachineAvailabilityStatus.LOADING) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--loading`])}
      >
        {renderStatusRow(
          <SpinnerWithVariant variant={["small", "secondary"]} />,
          isCreatingMachine
            ? texts.creatingMachine
            : texts.checkingMachineAvailability,
          "loading",
        )}
      </div>
    );
  }

  if (status === MachineAvailabilityStatus.IP_REACHABLE) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--loading`])}
      >
        {renderStatusRow(
          <SpinnerWithVariant variant={["small", "secondary"]} />,
          texts.checkingMachineAvailability,
          "loading",
        )}
        {renderStatusRow(
          <SpinnerWithVariant variant={["small", "secondary"]} />,
          texts.skillsIdentified,
          "loading",
        )}
      </div>
    );
  }

  if (status === MachineAvailabilityStatus.WARNING) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--warning`])}
      >
        {renderStatusRow(null, errorMessage || "", "warning")}
        {onRetry && (
          <Button
            fun={handleFooterButtonClick(onRetry)}
            text={texts.retry}
            variant={["no-outline-with-green20-hover", "color-blue"]}
          />
        )}
      </div>
    );
  }

  if (status === MachineAvailabilityStatus.SUCCESS) {
    const hasSkills = Boolean(skillsLabel?.trim()) && !showNoSkillsIdentified;

    let skillsText = texts.skillsIdentified;
    let skillsVariant: "warning" | "success" | "error" | undefined;

    if (showNoSkillsIdentified) {
      skillsText = texts.noMachineSkillsFoundMessage;
      skillsVariant = "error";
    } else if (hasSkills) {
      skillsText = `${texts.skillsIdentified} (${skillsLabel})`;
      skillsVariant = "success";
    }

    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--success`])}
      >
        {renderStatusRow(
          <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
          texts.machineIpReachable,
          "success",
        )}
        {renderStatusRow(
          hasSkills ? (
            <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />
          ) : showNoSkillsIdentified ? (
            <img src={closeRedIcon} alt={texts.statusIconAltText} />
          ) : null,
          skillsText,
          skillsVariant,
        )}
        {onRetry && showRetryForMissingSkills && (
          <Button
            fun={handleFooterButtonClick(onRetry)}
            text={texts.retry}
            variant={["no-outline-with-green20-hover", "color-blue"]}
          />
        )}
      </div>
    );
  }

  const protocolEntries = getGatewayOperationProtocolEntries(failureDetails);
  const opcUaErrorDescriptions = getOpcUaErrorDescriptions(
    failureDetails?.additional_error_codes,
    language,
  );
  const hasProtocolDetails =
    protocolEntries.length > 0 || opcUaErrorDescriptions.length > 0;

  return (
    <div className={cn(styles[cssClassName], styles[`${cssClassName}--error`])}>
      {renderStatusRow(
        <img src={closeRedIcon} alt={texts.statusIconAltText} />,
        errorMessage || "",
        "error",
        showProtocolDetails,
      )}
      {!hideSupportActions && (
        <FlexRowContainer horizontalGap={8} justifyContent="space-around">
          {onShowProtocol && hasProtocolDetails && (
            <Button
              fun={handleFooterButtonClick(onShowProtocol)}
              text={
                showProtocolDetails ? texts.hideProtocol : texts.showProtocol
              }
              variant={["no-outline-with-green20-hover", "color-blue"]}
            />
          )}
          {onRetry && (
            <Button
              fun={handleFooterButtonClick(onRetry)}
              text={texts.retry}
              variant={["no-outline-with-green20-hover", "color-blue"]}
            />
          )}
          {onGetSupport && (
            <Button
              fun={handleFooterButtonClick(onGetSupport)}
              text={texts.getSupport}
              variant={["no-outline-with-green20-hover", "color-blue"]}
            />
          )}
        </FlexRowContainer>
      )}
      {showProtocolDetails && hasProtocolDetails && (
        <OperationProtocolDetails
          className={styles[`${cssClassName}__protocol-details`]}
          protocolEntries={protocolEntries}
          opcUaErrorDescriptions={opcUaErrorDescriptions}
          opcUaErrorDetailsTitle={texts.opcUaErrorDetailsTitle}
        />
      )}
    </div>
  );
};
